import { useMemo } from 'react';
import type { KanbanCartao, KanbanQuadro, NivelPrioridadeKanban } from '@pieltec/contracts';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  CircleDot,
  Clock,
  PauseCircle,
  Ban,
  RotateCcw,
  CheckCircle2,
  CalendarX,
  AlertTriangle,
  ArrowUp,
  GripVertical,
  AlertOctagon,
  type LucideIcon,
} from 'lucide-react';
import {
  rotuloEstado,
  iconeUrgencia,
  formatarProgresso,
  rotuloPecasAFrente,
  formatarPrazo,
  chipsSubtipo,
  mensagemCabineIncompativel,
  textoCabinesCompativeis,
} from '../../../lib/kanban/apresentacao';
import { KanbanCartaoMenu } from './kanban-cartao-menu';
import { StatusIndicator } from '../../../components/ui/status-indicator';
import type { DestinoMover } from '../../../lib/kanban/movimento';
import '../../../styles/kanban-cartao.css';
import { apresentarAlertaCancelamento } from '../../../lib/pcp/alertas-cancelamento';

const ICONS: Record<string, LucideIcon> = {
  CircleDot,
  Clock,
  PauseCircle,
  Ban,
  RotateCcw,
  CheckCircle2,
  CalendarX,
  AlertTriangle,
  ArrowUp,
};

interface KanbanCartaoProps {
  cartao: KanbanCartao;
  onSelecionar?: (cartao: KanbanCartao) => void;
  podeArrastar?: boolean;
  quadro?: KanbanQuadro;
  onMover?: (cartao: KanbanCartao, destino: DestinoMover) => void;
  onPrioridade?: (cartao: KanbanCartao, nivel: NivelPrioridadeKanban) => void;
  onResolverConflito?: (cartao: KanbanCartao) => void;
  onDesbloquear?: (cartao: KanbanCartao) => void;
}

export function KanbanCartaoComponent({
  cartao,
  onSelecionar,
  podeArrastar = false,
  quadro,
  onMover,
  onPrioridade,
  onResolverConflito,
  onDesbloquear,
}: KanbanCartaoProps) {
  const estadoInfo = useMemo(() => rotuloEstado(cartao.estado), [cartao.estado]);
  const iconeUrgenciaNome = useMemo(
    () => iconeUrgencia(cartao.nivelPrioridade, cartao.situacaoPrazo),
    [cartao.nivelPrioridade, cartao.situacaoPrazo]
  );

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: cartao.chave,
    disabled: !podeArrastar,
  });

  const EstadoIcon = ICONS[estadoInfo.icone];
  const UrgenciaIcon = iconeUrgenciaNome ? ICONS[iconeUrgenciaNome] : null;
  const chips = chipsSubtipo(cartao);
  const urgencia = cartao.situacaoPrazo === 'estourado' ? 'Prazo vencido'
    : cartao.nivelPrioridade === 'urgente' ? 'Urgente'
    : cartao.nivelPrioridade === 'alta' ? 'Prioridade alta'
    : cartao.situacaoPrazo === 'perto' ? 'Prazo próximo' : null;
  const alertaFiscal = apresentarAlertaCancelamento(cartao.alertaCancelamentoFiscal === true);

  return (
    <div
      ref={setNodeRef}
      data-cartao-chave={cartao.chave}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'opacity-60' : undefined}
    >
    <Card
      className="kanban-card relative cursor-pointer overflow-hidden gap-0 py-0 shadow-none"
      data-estado={cartao.estado}
      onClick={() => {
        if (isDragging) return;
        onSelecionar?.(cartao);
      }}
    >
      <CardContent className="flex w-full flex-col gap-3 p-3">
        <div className="flex justify-between items-start gap-2">
          {podeArrastar && (
            <button
              type="button"
              className="kanban-card-control inline-flex shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 touch-none"
              {...attributes}
              {...listeners}
              aria-label={`Arrastar cartão ${cartao.loteCodigo} / ${cartao.opNumero}`}
              aria-roledescription="alça de arrastar"
              onClick={(event) => event.stopPropagation()}
            >
              <GripVertical className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <div className="flex-1 overflow-hidden">
            <h4 className="text-sm font-semibold" title={`${cartao.loteCodigo} / ${cartao.opNumero}`}>
              <button type="button" className="kanban-card-title" disabled={!onSelecionar}
                aria-label={`Abrir detalhes de ${cartao.loteCodigo} / ${cartao.opNumero}`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!isDragging) onSelecionar?.(cartao);
                }}>
                {cartao.loteCodigo} <span className="font-normal text-muted-foreground">/</span> {cartao.opNumero}
              </button>
            </h4>
            <p className="text-xs text-slate-500 truncate" title={`${cartao.clienteNome} • ${cartao.itemCodigo}`}>
              {cartao.clienteNome} &bull; {cartao.itemCodigo}
            </p>
          </div>
          {podeArrastar && quadro && onMover && onPrioridade && onResolverConflito && (
            <KanbanCartaoMenu
              cartao={cartao}
              quadro={quadro}
              onMover={onMover}
              onPrioridade={onPrioridade}
              onResolverConflito={onResolverConflito}
              onDesbloquear={onDesbloquear}
            />
          )}
        </div>

        <div className="kanban-card-metrics">
          <span><span className="kanban-card-label">Produção</span><strong>{formatarProgresso(cartao.quantidadeProduzida, cartao.quantidadeAlvo)}</strong></span>
          <span><span className="kanban-card-label">Prazo</span><time dateTime={cartao.prazo}>{formatarPrazo(cartao.prazo)}</time></span>
        </div>
        {rotuloPecasAFrente(cartao) && (
          <p className="text-xs font-medium text-blue-800" data-testid="kanban-card-a-frente">{rotuloPecasAFrente(cartao)}</p>
        )}
        {urgencia && (
          <p className="kanban-card-urgency" data-atrasado={cartao.situacaoPrazo === 'estourado'}>
            {UrgenciaIcon && <UrgenciaIcon className="size-3.5 shrink-0" aria-hidden="true" />}
            {urgencia}
          </p>
        )}

        {cartao.bloqueio && (
          <div className="flex items-start gap-2 rounded border border-border bg-muted px-2 py-1.5">
            <StatusIndicator tone="danger" icon={<Ban className="size-3.5 shrink-0" aria-hidden="true" />} label="Bloqueado" />
            <div className="flex flex-col">
              <span className="text-xs text-foreground">{cartao.bloqueio.tipoInterrupcaoNome === 'OUTRO' && cartao.bloqueio.motivo
                ? cartao.bloqueio.motivo
                : (cartao.bloqueio.tipoInterrupcaoNome ?? cartao.bloqueio.motivo)}</span>
            </div>
          </div>
        )}

        {alertaFiscal && (
          <a href={alertaFiscal.destino} onClick={(event) => event.stopPropagation()} className="flex min-h-11 items-start gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            <span><strong>{alertaFiscal.rotulo}.</strong> {alertaFiscal.mensagem}</span>
          </a>
        )}

        {(chips.length > 0 || cartao.corCodigo) && (
          <div className="flex flex-wrap gap-1">
            {chips.map((chip) => (
              <span key={chip} className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-semibold text-slate-700">
                {chip}
              </span>
            ))}
            {cartao.corCodigo && (
              <span className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-semibold text-slate-700">
                {cartao.corCodigo}
              </span>
            )}
          </div>
        )}

        {cartao.atributosIncompletos && (
          <div className="text-xs font-medium text-amber-800"><p className="flex items-start gap-1"><AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />{cartao.atributosPendentes?.length ? `Sem sugestão de cabine. Falta informar: ${cartao.atributosPendentes.join(', ')}.` : 'Sem sugestão de cabine. Confira os atributos de pintura na ficha da peça.'}</p>{cartao.materialId ? <a className="inline-flex min-h-11 items-center underline" onClick={(evento) => evento.stopPropagation()} href={`/cadastros/pecas/${encodeURIComponent(cartao.materialId)}#dados-da-peca`}>Corrigir na ficha da peça</a> : <p>Peça ao PCP para revisar os dados técnicos desta peça em Cadastros → Peças / Produtos.</p>}</div>
        )}

        {cartao.compatibilidade && !cartao.compatibilidade.compativel && (
          <StatusIndicator tone="warning" icon={<AlertTriangle className="size-3 shrink-0" aria-hidden="true" />} label={mensagemCabineIncompativel(cartao.compatibilidade.regrasFalhas)} />
        )}

        {textoCabinesCompativeis(cartao.cabinesSugeridas) && (
          <p className="text-xs text-slate-600">{textoCabinesCompativeis(cartao.cabinesSugeridas)}</p>
        )}

        <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-2">
          <StatusIndicator tone={estadoInfo.tone} icon={EstadoIcon ? <EstadoIcon className="size-3" aria-hidden="true" /> : undefined} label={estadoInfo.texto} />
          {cartao.nivelPrioridade !== 'normal' && (
            <Badge variant="secondary" className="text-xs">
              {cartao.nivelPrioridade}
            </Badge>
          )}
          {cartao.conflito && (
            <StatusIndicator tone="danger" icon={<AlertOctagon className="size-3 shrink-0" aria-hidden="true" />} label="Conflito" />
          )}
        </div>
      </CardContent>
    </Card>
    </div>
  );
}
